import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BreadCrumb from 'Common/BreadCrumb';
import { Link } from 'react-router-dom';
import { Dropdown } from 'Common/Components/Dropdown';
import TableContainer from 'Common/TableContainer';
import Select from 'react-select';

// Icons
import { Search, Trash2, Plus, MoreHorizontal, FileEdit } from 'lucide-react';
import Modal from 'Common/Components/Modal';
import DeleteModal from 'Common/DeleteModal';

// Images

// react-redux
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

// Formik
import * as Yup from "yup";
import { useFormik } from "formik";

import {
  onAddPromo,
  onDeletePromo,
  onGetPromoList,
  onUpdatePromo,
} from 'slices/thunk';
import { ToastContainer } from 'react-toastify';
import filterDataBySearch from 'Common/filterDataBySearch';
import { PromoModel } from '../../../services/maintenance.service';
import { doFileUpload } from '../../../services/file.service';
import DropZoneImage from '../../../Common/Components/Dropzone';
import { StatusHelper } from '../../../Common/Components/Status';

const PromoPage = () => {
  const dispatch = useDispatch<any>();

  const selectDataList = createSelector(
    (state: any) => state.Maintenances,
    (maintenance) => ({
      promoList: maintenance.promoList
    })
  );

  const { promoList } = useSelector(selectDataList);
  const [promo, setPromo] = useState<PromoModel[]>([]);
  const [eventData, setEventData] = useState<any>()
  const [show, setShow] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const [selectfiles, setSelectfiles] = useState<Array<File & { base64: string }>>([]);

  // Get Data
  useEffect(() => {
    dispatch(onGetPromoList())
  }, [dispatch])

  useEffect(() => {
    setPromo(promoList)
  }, [promoList]);

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  // Delete Data
  const onClickDelete = (cell: any) => {
    setDeleteModal(true);
    if (cell.promoId) {
      setEventData(cell);
    }
  };

  const handleDelete = () => {
    if (eventData) {
      dispatch(onDeletePromo(eventData.promoId));
      setDeleteModal(false);
    }
  };

  // Update Data
  const handleUpdateDataClick = (ele: any) => {
    setEventData({ ...ele });
    setIsEdit(true);
    setShow(true);
  };

  // validation
  const validation: any = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      promoId: (eventData && eventData.promoId) || '',
      title: (eventData && eventData.title) || '',
      description: (eventData && eventData.description) || '',
      productId: (eventData && eventData.productId) || '',
      bannerMobileUrls: (eventData && eventData.bannerMobileUrls) || [],
      bannerMobileFiles: (eventData && eventData.bannerMobileFiles) || [],
      bannerUrls: (eventData && eventData.bannerUrls[0]) || [],
      remark: (eventData && eventData.remark) || '',
      status: (eventData && eventData.status) || 'active',
    },
    validationSchema: Yup.object({
      promoId: Yup.string().optional(),
      title: Yup.string().required("Please Enter Title"),
      description: Yup.string().optional(),
      productId: Yup.string().optional(),
      bannerMobileUrls: Yup.array().of(Yup.string()).optional(),
      bannerUrls: Yup.string().optional(),
      remark: Yup.string().optional(),
      status: Yup.string().required("Please Enter Status"),
    }),
    onSubmit: async (values) => {
      const image = []
      if (selectfiles.length > 0) {
        for (const file of selectfiles) {
          const fileObject = await doFileUpload(file.name, file.base64, `promo:${values.promoId}`)
          image.push(fileObject.s3Url)
        }
      }
      if (values.bannerMobileUrls.length > 0) image.push(...values.bannerMobileUrls)

      delete values.bannerMobileFiles

      const data = {
        ...values,
        bannerMobileUrls: image,
        bannerUrls: [values.bannerUrls],
      };
      if (isEdit) {
        dispatch(onUpdatePromo(data));
      } else {
        delete data.promoId;
        dispatch(onAddPromo(data));
      }
      toggle();
    },
  });

  //
  const toggle = useCallback(() => {
    if (show) {
      setShow(false);
      setEventData("");
      setIsEdit(false);
      setSelectfiles([]);
    } else {
      setShow(true);
      setEventData("");
      setSelectfiles([]);
      validation.resetForm();
    }
  }, [show, validation]);

  // Search Data
  const filterSearchData = (e: any) => {
    const search = e.target.value;
    const keysToSearch = ['name', 'promoType'];
    filterDataBySearch(promoList, search, keysToSearch, setPromo);
  };

  const statusDropdown = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'draft', label: 'Draft' },
    { value: 'expired', label: 'Expired' },
    { value: 'completed', label: 'Completed' },
  ]

  const columns = useMemo(() => [
    {
      header: (
        <div className="flex items-center h-full">
          <input id="CheckboxAll" className="w-4 h-4 bg-white border border-slate-200 checked:bg-none dark:bg-zink-700 dark:border-zink-500 rounded-sm appearance-none arrow-none relative after:absolute after:content-['\eb7b'] after:top-0 after:left-0 after:font-remix after:leading-none after:opacity-0 checked:after:opacity-100 after:text-custom-500 checked:border-custom-500 dark:after:text-custom-500 dark:checked:border-custom-800 cursor-pointer" type="checkbox" />
        </div>
      ),
      enableSorting: false,
      id: "checkAll",
      cell: (cell: any) => {
        return (
          <div className="flex items-center h-full">
            <input id="Checkbox1" className="w-4 h-4 bg-white border border-slate-200 checked:bg-none dark:bg-zink-700 dark:border-zink-500 rounded-sm appearance-none arrow-none relative after:absolute after:content-['\eb7b'] after:top-0 after:left-0 after:font-remix after:leading-none after:opacity-0 checked:after:opacity-100 after:text-custom-500 checked:border-custom-500 dark:after:text-custom-500 dark:checked:border-custom-800 cursor-pointer" type="checkbox" />
          </div>
        );
      }
    },
    {
      header: "Name",
      accessorKey: "title",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div className="flex items-center gap-2">
          <div className="flex flex-row items-center gap-2">
            <div className="flex items-center justify-center w-10 h-10 font-medium rounded-full shrink-0 bg-slate-200 text-slate-800 dark:text-zink-50 dark:bg-zink-600">
              <img src={cell.row.original.bannerMobileUrls?.[0]} alt="" className="h-10 rounded-full" />
            </div>
            <h6 className="mb-1">{cell.getValue()}</h6>
          </div>
        </div>
      )
    },
    {
      header: "Status",
      accessorKey: "status",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <StatusHelper status={cell.getValue()} />
      )
    },
    {
      header: "Action",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (cell: any) => (
        <Dropdown className="relative">
          <Dropdown.Trigger className="flex items-center justify-center w-[30px] h-[30px] p-0 text-slate-500 btn bg-slate-100 hover:text-white hover:bg-slate-600 focus:text-white focus:bg-slate-600 focus:ring focus:ring-slate-100 active:text-white active:bg-slate-600 active:ring active:ring-slate-100 dark:bg-slate-500/20 dark:text-slate-400 dark:hover:bg-slate-500 dark:hover:text-white dark:focus:bg-slate-500 dark:focus:text-white dark:active:bg-slate-500 dark:active:text-white dark:ring-slate-400/20" id={cell.row.original.serviceId}>
            <MoreHorizontal className="w-3 h-3" />
          </Dropdown.Trigger>
          <Dropdown.Content placement="right-end" className="absolute z-50 py-2 mt-1 ltr:text-left rtl:text-right list-none bg-white rounded-md shadow-md min-w-[10rem] dark:bg-zink-600" aria-labelledby={cell.row.original.serviceId}>
            <li>
              <Link data-modal-target="addUserModal" className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200" to="#!"
                onClick={() => {
                  const data = cell.row.original;
                  handleUpdateDataClick(data);
                }}>
                <FileEdit className="inline-block w-3 h-3 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">Edit</span></Link>
            </li>
            {
              cell.row.original.status === 'active' &&
              <li>
                <Link className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200" to="#!" onClick={() => {
                  const data = cell.row.original;
                  onClickDelete(data);
                }}><Trash2 className="inline-block w-3 h-3 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">Delete</span></Link>
              </li>
            }
          </Dropdown.Content>
        </Dropdown>
      ),
    }
  ], [])

  const handleChange = (selectedOption: any) => {
    if (selectedOption.value === 'Select Status' || selectedOption.value === 'Hidden') {
      setPromo(promoList);
    } else {
      const filteredPromos = promoList.filter((data: any) => data.status === selectedOption.value);
      setPromo(filteredPromos);
    }
  };

  return (
    <React.Fragment>
      <BreadCrumb title='Promotion' pageTitle='Maintenances' />
      <DeleteModal show={deleteModal} onHide={deleteToggle} onDelete={handleDelete} />
      <ToastContainer closeButton={false} limit={1} />
      <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12">
        <div className="xl:col-span-12">
          <div className="card" id="usersTable">
            <div className="card-body">
              <div className="flex items-center">
                <h6 className="text-15 grow">Promotion</h6>
                <div className="shrink-0">
                  <button type="button" className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20" onClick={toggle}><Plus className="inline-block w-4 h-4" />
                    <span className="align-middle">Add Promo</span>
                  </button>
                </div>
              </div>
            </div>
            <div className="!py-3.5 card-body border-y border-dashed border-slate-200 dark:border-zink-500">
              <form action="#!">
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
                  <div className="relative xl:col-span-2">
                    <input type="text" className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Search for name, email, phone number etc..." autoComplete="off" onChange={(e) => filterSearchData(e)} />
                    <Search className="inline-block w-4 h-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                  </div>
                  <div className="xl:col-span-2">
                    <Select
                      className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      options={statusDropdown}
                      isSearchable={false}
                      onChange={(event: any) => handleChange(event)}
                      id="choices-single-default"
                    />
                  </div>
                </div>
              </form>
            </div>
            <div className="card-body">
              {promo && promo.length > 0 ?
                <TableContainer
                  isPagination={true}
                  columns={(columns || [])}
                  data={(promo || [])}
                  customPageSize={10}
                  divclassName="-mx-5 -mb-5 overflow-x-auto"
                  tableclassName="w-full border-separate table-custom border-spacing-y-1 whitespace-nowrap"
                  theadclassName="text-left relative rounded-md bg-slate-100 dark:bg-zink-600 after:absolute ltr:after:border-l-2 rtl:after:border-r-2 ltr:after:left-0 rtl:after:right-0 after:top-0 after:bottom-0 after:border-transparent [&.active]:after:border-custom-500 [&.active]:bg-slate-100 dark:[&.active]:bg-zink-600"
                  thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold"
                  tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5"
                  PaginationClassName="flex flex-col items-center mt-8 md:flex-row"
                />
                :
                (<div className="noresult">
                  <div className="py-6 text-center">
                    <Search className="w-6 h-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" />
                    <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                    <p className="mb-0 text-slate-500 dark:text-zink-200">We've searched more than 199+ users We did not find any users for you search.</p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>

      <Modal show={show} onHide={toggle} id="defaultModal" modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600">
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-300/20"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500">
          <Modal.Title className="text-16">{!!isEdit ? "Update Point" : "Add Point"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form action="#!" onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}>
            <div className="mb-3">
              <label htmlFor="titleInput" className="inline-block mb-2 text-base font-medium">Title</label>
              <input className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                type="text"
                id="titleInput"
                placeholder="Enter Title"
                name="title"
                onChange={validation.handleChange}
                value={validation.values.title || ""}
              />
              {validation.touched.title && validation.errors.title ? (
                <p className="text-red-400">{validation.errors.title}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="descriptionInput" className="inline-block mb-2 text-base font-medium">Description</label>
              <input className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                type="text"
                id="descriptionInput"
                placeholder="Enter Description"
                name="description"
                onChange={validation.handleChange}
                value={validation.values.description || ""}
              />
              {validation.touched.description && validation.errors.description ? (
                <p className="text-red-400">{validation.errors.description}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="bannerMobileUrlsInput" className="inline-block mb-2 text-base font-medium">Mobile Banner</label>
              <DropZoneImage
                selectfiles={selectfiles}
                setSelectfiles={setSelectfiles}
                formikData={validation}
                fieldName='bannerMobileUrls'
                files={validation.values.bannerMobileFiles}
                filesType='url'
              />
              {validation.touched.bannerMobileUrls && validation.errors.bannerMobileUrls ? (
                <p className="text-red-400">{validation.errors.bannerMobileUrls}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="bannerUrlsInput" className="inline-block mb-2 text-base font-medium">Banner Link</label>
              <input className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                type="text"
                id="bannerUrlsInput"
                placeholder="Enter Banner URL"
                name="bannerUrls"
                onChange={validation.handleChange}
                value={validation.values.bannerUrls || ""}
              />
              {validation.touched.bannerUrls && validation.errors.bannerUrls ? (
                <p className="text-red-400">{validation.errors.bannerUrls}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="statusInput" className="inline-block mb-2 text-base font-medium">Status</label>
              <Select
                className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                options={statusDropdown}
                isSearchable={false}
                name="status"
                id="statusInput"
                onChange={selectedOption => {
                  validation.setFieldValue(`status`, selectedOption?.value)
                }}
                value={statusDropdown.find(status => { return status.value === validation.values.status })}
              />
              {validation.touched.status && validation.errors.status ? (
                <p className="text-red-400">{validation.errors.status}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="remarkInput" className="inline-block mb-2 text-base font-medium">Remark</label>
              <input className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                type="text"
                id="remarkInput"
                placeholder="Enter Remark"
                name="remark"
                onChange={validation.handleChange}
                value={validation.values.remark || ""}
              />
              {validation.touched.remark && validation.errors.remark ? (
                <p className="text-red-400">{validation.errors.remark}</p>
              ) : null}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="reset"
                data-modal-close="addDocuments"
                className="text-red-500 transition-all duration-200 ease-linear bg-white border-white btn hover:text-red-600 focus:text-red-600 active:text-red-600 dark:bg-zink-500 dark:border-zink-500"
                onClick={toggle}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              >
                {!!isEdit ? "Update Promo" : "Add Promo"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default PromoPage;
